import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, fmtDate } from "@/lib/api";
import { Plus, Printer, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

export default function Bills() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  const load = () => api.get("/bills").then(({data}) => setItems(data)).catch((e)=>toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this bill?")) return;
    await api.delete(`/bills/${id}`); toast.success("Deleted"); load();
  };

  const PAY_STYLES = {
    Unpaid: "bg-red-100 text-red-700",
    Partial: "bg-amber-100 text-amber-800",
    Paid: "bg-green-100 text-green-700",
  };

  const setPayment = async (b, payment_status) => {
    let amount_received = "0";
    if (payment_status === "Partial") {
      const v = window.prompt(`Amount received for bill ${b.bill_no} (Grand Total ₹${b.grand_total}):`, b.amount_received || "");
      if (v === null) return;
      amount_received = v;
    }
    try {
      const { data } = await api.put(`/bills/${b.id}/payment`, { payment_status, amount_received });
      setItems((xs) => xs.map((x) => (x.id === b.id ? { ...x, payment_status, amount_received: data.amount_received } : x)));
      toast.success(`${b.bill_no} marked ${payment_status}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = items.filter(b => !q || `${b.bill_no} ${b.party_name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-8" data-testid="bills-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl text-slate-900">Freight Bills</h1>
          <p className="text-slate-500 text-sm">Invoices raised to parties</p>
        </div>
        <Link to="/portal/bills/new" className="btn-primary px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2" data-testid="new-bill-btn">
          <Plus size={16}/> New Freight Bill
        </Link>
      </div>

      <div className="bg-white border border-slate-100 rounded-xl">
        <div className="p-4 border-b border-slate-100">
          <Input placeholder="Search by bill no or party…" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="bills-search"/>
        </div>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-2">Bill No</th>
              <th className="text-left px-4 py-2">Date</th>
              <th className="text-left px-4 py-2">Party</th>
              <th className="text-right px-4 py-2">Grand Total</th>
              <th className="text-left px-4 py-2">Payment</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No bills yet.</td></tr>}
            {filtered.map(b => (
              <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2 font-mono text-[#0b5cc4]">{b.bill_no}</td>
                <td className="px-4 py-2">{fmtDate(b.date)}</td>
                <td className="px-4 py-2">{b.party_name}</td>
                <td className="px-4 py-2 text-right">₹ {b.grand_total}</td>
                <td className="px-4 py-2">
                  <select
                    value={b.payment_status || "Unpaid"}
                    onChange={(e) => setPayment(b, e.target.value)}
                    className={`text-xs font-semibold rounded-full px-2.5 py-1 border-0 cursor-pointer appearance-none ${PAY_STYLES[b.payment_status || "Unpaid"]}`}
                    data-testid={`payment-select-${b.bill_no}`}
                  >
                    <option>Unpaid</option>
                    <option>Partial</option>
                    <option>Paid</option>
                  </select>
                  {b.payment_status === "Partial" && (
                    <div className="text-[10px] text-slate-400 mt-0.5">₹{b.amount_received} received</div>
                  )}
                </td>
                <td className="px-4 py-2">
                  <div className="flex justify-end gap-1">
                    <Link to={`/portal/bills/${b.id}/print`} className="p-2 hover:bg-slate-100 rounded"><Printer size={16}/></Link>
                    <Link to={`/portal/bills/${b.id}/edit`} className="p-2 hover:bg-slate-100 rounded"><Pencil size={16}/></Link>
                    <button onClick={()=>remove(b.id)} className="p-2 hover:bg-red-50 rounded text-red-600"><Trash2 size={16}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}
